import { useState } from 'react'
import { Bell } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from 'react-oidc-context'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { NotificationItem } from '@/components/notifications/notification-item'
import {
  useMyNotifications,
  useUnreadNotificationCount,
} from '@/features/notifications/use-notifications'
import { cn } from '@/lib/utils'

const POPOVER_PAGE_SIZE = 5

/**
 * Phase 14 — navbar bell. Auth-only mount; the {@code useUnreadNotificationCount} hook is also
 * gated on auth as defense-in-depth (it'd 401 anyway, but skipping the request is cleaner).
 */
export const NotificationsBell = () => {
  const auth = useAuth()
  const isAuthenticated = auth.isAuthenticated
  const [open, setOpen] = useState(false)

  const { data: count } = useUnreadNotificationCount(isAuthenticated)
  const inbox = useMyNotifications(0, POPOVER_PAGE_SIZE)

  if (!isAuthenticated) return null

  const unread = count?.count ?? 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
          className="relative"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              className={cn(
                'absolute right-1 top-1 h-2 w-2 rounded-full bg-destructive',
                'ring-2 ring-background'
              )}
              aria-hidden
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-b border-border px-3 py-2 text-sm font-medium">Notifications</div>
        {!open ? null : inbox.isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !inbox.data || inbox.data.items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            No notifications yet.
          </p>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {inbox.data.items.map((n) => (
              <NotificationItem key={n.id} notification={n} compact />
            ))}
          </div>
        )}
        <div className="border-t border-border px-3 py-2 text-right">
          <Link
            to="/notifications"
            className="text-xs underline-offset-2 hover:underline"
            onClick={() => setOpen(false)}
          >
            View all →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  )
}
